import { Box, Stack, Typography } from '@mui/material';
import AirplanemodeActiveOutlined from '@mui/icons-material/AirplanemodeActiveOutlined';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import { ActivityList } from '../../components/ActivityList';
import { DataTable } from '../../components/DataTable';
import { DonutChart } from '../../components/DonutChart';
import { EmptyState } from '../../components/EmptyState';
import { contactRows } from '../../components/contactRows';
import { InfoPanel as SharedInfoPanel } from '../../components/InfoPanel';
import { Panel } from '../../components/Panel';
import { PerformanceBar } from '../../components/PerformanceBar';
import { SrbCode } from '../../components/SrbCode';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { shortDate } from '../../lib/dates';
import { useShipments } from '../shipments/useShipments';
import { SERVICE_NAME } from '../../lib/services';
import { OUTCOME_TONE, type Agent, type Rfq } from './sample';
import type { AgentStats } from './stats';

export const InfoPanel = ({ a }: { a: Agent }) => (
  <SharedInfoPanel
    title="Agent Information"
    rows={[
      ...contactRows(a),
      { label: 'Services', value: a.services.join(', ') || '–' },
      { label: 'Coverage', value: a.coverage || '–' },
      { label: 'Industry', value: a.industry },
      { label: 'Notes', value: a.notes || '–' },
    ]}
  />
);

/** How the agent performs, counted from the recorded rates and deliveries (see agents/stats.ts for each definition) */
export function PerformancePanel({ st }: { st: AgentStats }) {
  return (
    <Panel
      title="Performance Overview"
      subtitle="Transit reliability: delivered shipments that arrived on time. Price competitiveness: how close the agent's rates are to the lowest rate on the same request."
    >
      {st.total ? (
        <Stack spacing={1.5}>
          <PerformanceBar label="Response Rate" kpi="responseRate" value={st.responseRate} tone="success" />
          <PerformanceBar label="Win Rate" kpi="winRate" value={st.winRate} tone="primary" />
          <PerformanceBar
            label="Transit Reliability"
            kpi="transitReliability"
            value={st.transitReliability}
            tone="info"
          />
          <PerformanceBar
            label="Price Competitiveness"
            kpi="priceCompetitiveness"
            value={st.priceCompetitiveness}
            tone="warning"
          />
        </Stack>
      ) : (
        <EmptyState>No requests for rates yet.</EmptyState>
      )}
    </Panel>
  );
}

export function OutcomesPanel({ st }: { st: AgentStats }) {
  return (
    <Panel title="Shipment Outcomes">
      {st.total ? (
        <DonutChart
          centerLabel="Total RFQs"
          slices={[
            { label: 'Won', value: st.won, tone: 'success' },
            { label: 'Lost', value: st.lost, tone: 'error' },
            { label: 'Pending', value: st.pending, tone: 'warning' },
            { label: 'Not Quoted', value: st.notQuoted, tone: 'neutral' },
          ]}
        />
      ) : (
        <EmptyState>No requests for rates yet.</EmptyState>
      )}
    </Panel>
  );
}

const SERVICE_ICON = {
  FCL: <InventoryOutlined />,
  LCL: <InventoryOutlined />,
  Air: <AirplanemodeActiveOutlined />,
  Road: <LocalShippingOutlined />,
};

export function ServicesPanel({ a }: { a: Agent }) {
  return (
    <Panel title="Services Offered">
      {a.services.length ? (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))' }}>
          {a.services.map((s) => (
            <Stack
              key={s}
              spacing={0.5}
              sx={{ alignItems: 'center', textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}
            >
              <Box sx={{ color: toneColor('info'), display: 'flex' }}>
                {s === 'Air' || s === 'Road' || s === 'FCL' || s === 'LCL' ? (
                  SERVICE_ICON[s]
                ) : (
                  <DirectionsBoatOutlined />
                )}
              </Box>
              <Typography sx={{ fontWeight: 700 }}>{s}</Typography>
              <Typography variant="caption" color="text.secondary">
                {SERVICE_NAME[s]}
              </Typography>
            </Stack>
          ))}
        </Box>
      ) : (
        <EmptyState>No services listed.</EmptyState>
      )}
    </Panel>
  );
}

/** "USD" for a container, "USD/kg" for air, "USD/CBM" for LCL */
const unit = (r: Rfq, mode?: string) =>
  mode === 'Air' ? `${r.currency}/kg` : mode === 'LCL' ? `${r.currency}/CBM` : r.currency;
const amount = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

type RateProps = { rfqs: Rfq[]; title?: string; limit?: number };

/** Every price this agent gave, newest first: the record you keep for each agent */
export function RateHistoryPanel({ rfqs, title = 'Recent Rate History', limit }: RateProps) {
  const rows = [...rfqs].sort((x, y) => y.date.localeCompare(x.date)).slice(0, limit ?? rfqs.length);
  const { items: shipments } = useShipments();
  const ship = (r: Rfq) => shipments.find((s) => s.srb === r.srb);
  return (
    <Panel title={title}>
      <DataTable<Rfq>
        rows={rows}
        rowKey={(r) => r.agentId + r.srb + r.date}
        empty="No rates recorded yet."
        columns={[
          { header: 'Date', primary: true, nowrap: true, render: (r) => shortDate(r.date) },
          { header: 'Shipment', nowrap: true, render: (r) => <SrbCode code={r.srb} /> },
          { header: 'Route', render: (r) => (ship(r) ? `${ship(r)!.from.city} → ${ship(r)!.to.city}` : '–') },
          { header: 'Service', nowrap: true, render: (r) => ship(r)?.mode ?? '–' },
          { header: 'Rate', nowrap: true, render: (r) => (r.rate === null ? '–' : amount(r.rate)) },
          { header: 'Currency', nowrap: true, render: (r) => unit(r, ship(r)?.mode) },
          ...(limit ? [] : [{ header: 'Transit Time', nowrap: true, render: (r: Rfq) => r.transit || '–' }]),
          {
            header: 'Outcome',
            nowrap: true,
            action: true,
            render: (r) => <StatusChip label={r.outcome} tone={OUTCOME_TONE[r.outcome]} />,
          },
        ]}
      />
    </Panel>
  );
}

export function RecentActivityPanel({ rfqs }: { rfqs: Rfq[] }) {
  const { items: shipments } = useShipments();
  return (
    <ActivityList
      items={[...rfqs]
        .sort((x, y) => y.date.localeCompare(x.date))
        .slice(0, 5)
        .map((r) => {
          const s = shipments.find((x) => x.srb === r.srb);
          return {
            id: r.agentId + r.srb + r.date,
            tone: OUTCOME_TONE[r.outcome],
            date: shortDate(r.date),
            text:
              r.rate === null
                ? `Sent RFQ for ${r.srb}, no rate yet`
                : `Received ${s?.mode ?? ''} rate for ${r.srb}${s ? ` (${s.from.city} → ${s.to.city})` : ''}`,
          };
        })}
    />
  );
}
